import { IBD } from '@hub/free-form-hub/config';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useState } from 'react';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { useTranslation } from 'next-i18next';
import type { NbsResponse } from '@hub/free-form-hub/common/uploadFile/fetchNbsRecommendation';

export interface IbdRecommendationData {
  shape: string;
  articleName: string;
  exactCatalogMatch: boolean;
  articlesCount: number;
  shopUrl: string;
  article: {
    widen: string;
    shopUrl: string;
    unitSystem: 'metric' | 'imperial';
    articleNumber: string;
    unitPrice: {
      value: number;
      unit: string;
    };
    deliveryTime: {
      days: number;
      value: number;
      text: string;
    };
  };
  widen: {
    schemaShortKey: string;
    iconShortKey: string;
  };
}

export interface IbdRecommendation {
  success: boolean;
  data?: IbdRecommendationData;
}

export interface IbdRecommendationRequestPayload {
  request_id: string;
  category?: string;
  quantity?: number;
  dimensions?: Record<string, number>;
}

export const useIbdRecommendation = (quantity: number) => {
  const { t } = useTranslation(['free-form-hub/freeFormHub', 'common']);
  const { addToast } = useToasts();
  const [ibdRecommendation, setIbdRecommendations] = useState<IbdRecommendation>(undefined);

  const getIbdRecommendation = async (session: Session, nbsResponse: NbsResponse): Promise<void> => {
    const { sessionId, server } = session;
    const createRequestPayload = (response: NbsResponse): IbdRecommendationRequestPayload => ({
      category: response.category,
      dimensions: response.dimensions,
      quantity,
      request_id: response.request_id || response.oroox_id,
    });

    const response = await fetch(`${IBD.apiUrl}/nbs/article?sid=${sessionId}&server=${server}`, {
      method: 'POST',
      body: JSON.stringify(createRequestPayload(nbsResponse)),
      headers: {
        'Content-Type': 'application/json',
        'Kopla-Service-Id': IBD.koplaServiceId,
      },
    });

    if (!response.ok) {
      addToast({
        content: t('NETWORK_FAILURE', { ns: 'free-form-hub/freeFormHub' }),
        type: ToastType.ERROR,
        position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
      });
    } else {
      const recommendation = (await response.json()) as IbdRecommendation;
      setIbdRecommendations(recommendation);
    }
  };

  return { getIbdRecommendation, setIbdRecommendations, ibdRecommendation };
};
